import React, { useState } from 'react';
import { createBlogPost } from '../services/blog';
import AnimatedSection from './AnimatedSection';

interface BlogUploadProps {
  onUploadSuccess?: () => void;
}

const BlogUpload: React.FC<BlogUploadProps> = ({ onUploadSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    image: null as File | null,
    title: '',
    content: '',
    category: '',
    publishedAt: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'file') {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setFormData(prev => ({ ...prev, image: file }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      setError('Title and content are required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formDataToSend = new FormData();
      if (formData.image) formDataToSend.append('image', formData.image);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('content', formData.content);
      if (formData.category) formDataToSend.append('category', formData.category);
      if (formData.publishedAt) {
        formDataToSend.append('publishedAt', new Date(formData.publishedAt).toISOString());
      }

      const response = await createBlogPost(formDataToSend);
      setSuccess(`Blog post created successfully! ID: ${response.data._id}`);
      
      // Reset form
      setFormData({
        image: null,
        title: '',
        content: '',
        category: '',
        publishedAt: '',
      });
      const fileInput = document.getElementById('blog-image') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      // Notify parent to refresh list
      if (onUploadSuccess) {
        setTimeout(() => {
          onUploadSuccess();
        }, 1000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create blog post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedSection animation="fade-in">
      <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20">
        <h3 className="text-2xl font-bold text-dark-900 dark:text-neutral-50 mb-6">Create New Blog Post</h3>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 text-red-600 dark:text-red-400 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-500/10 border border-green-500/50 text-green-600 dark:text-green-400 rounded-lg">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
              Featured Image (Optional)
            </label>
            <input
              type="file"
              id="blog-image"
              accept="image/*"
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
              placeholder="Blog post title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
              Content *
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              required
              rows={12}
              className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
              placeholder="Write your blog post content here..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
                Category
              </label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
                placeholder="e.g., Technology"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-900 dark:text-neutral-50 mb-2">
                Published Date
              </label>
              <input
                type="date"
                name="publishedAt"
                value={formData.publishedAt}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-primary-500/20 rounded-lg bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-6 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
          >
            {loading ? 'Creating...' : 'Create Blog Post'}
          </button>
        </form>
      </div>
    </AnimatedSection>
  );
};

export default BlogUpload;

